/**
 * Contract Category Settings Page — /office/settings/contract-categories/
 *
 * Wires the create/edit dynamic modal AND the drag-and-drop reorder
 * for ``ContractCategory`` (PR-6c-4, 2026-05 — drag follow-up
 * 2026-05-26). Follows the canonical form-modal pattern documented
 * in ``.cursor/rules/form-modals.mdc``.
 *
 * Triggers (delegated on ``document.body``):
 *   - ``[data-contract-category-modal-open][data-contract-category-modal-mode="create"]``
 *     — header "Add category" button.
 *   - ``[data-contract-category-modal-open][data-contract-category-modal-mode="edit"]``
 *     — per-row "Edit" button in ``_table.html``. URL is read from
 *     ``data-contract-category-edit-url``.
 *
 * Template contract on the root container:
 *   ``[data-auto-init="contractCategorySettings"
 *      data-contract-category-create-url="..."
 *      data-contract-category-modal-create-title="..."
 *      data-contract-category-modal-edit-title="..."]``
 *
 * Drag-and-drop reorder:
 *   - ``<tbody data-contract-category-reorder data-reorder-url="...">``
 *     hosts the rows.
 *   - Each row carries ``data-cat-id="<pk>"``. Active rows have a
 *     ``.cc-drag-handle`` span (grip icon); archived rows don't, so
 *     SortableJS's ``handle`` option blocks the drag silently.
 *   - On drop, we POST every row's id in the new visual order to
 *     ``data-reorder-url``. The server reassigns ``display_order =
 *     10, 20, 30 ...`` in a transaction. We POST archived rows too
 *     (they keep a display_order on the model) for an internally
 *     consistent table state — but since they're not in the
 *     « Type de service » picker, their order has no visible
 *     downstream effect.
 *
 * Lifecycle (cf. hx-boost-init.mdc rules 1, 3, 4):
 *   - ``bindGlobalDelegation`` registers the body-level click +
 *     ``htmx:afterSettle`` listeners exactly once for the SPA lifetime
 *     (module-level ``globalBound`` flag — rule 1: global listener,
 *     module-level guard).
 *   - The modal helper is created at ``initContractCategorySettings()``
 *     time, bound to the static ``#contract-category-modal`` shell. On
 *     a boosted swap of ``#main-content``, the shell is replaced by a
 *     fresh DOM node with the same id; the cached helper now points at
 *     an orphan, so we null it out and re-init (§ 9 of
 *     ``form-modals.mdc``).
 *   - The Sortable instance is per-tbody. The ``<tbody>`` is swapped
 *     by HTMX on every Archive / Reactivate toggle (and by the soft
 *     reload after create / edit), so we tag each ``<tbody>`` with a
 *     per-node JS flag (``tbodyEl._ccSortableBound``) and re-init
 *     anything not yet bound on every ``htmx:afterSettle`` AND once
 *     at page init. Per-element flag is intentional per rule 4 — a
 *     module-level flag would short-circuit the second visit and
 *     leave the fresh tbody without drag wiring.
 */

import Sortable from 'sortablejs';
import { createDynamicModal } from '../utils/ui/dynamic-modal.js';

let globalBound = false;
let modalHelper = null;

function getCsrfToken() {
    const el = document.querySelector('[name=csrfmiddlewaretoken]');
    if (el && el.value) return el.value;
    const m = document.cookie.match(/(?:^|;\s*)csrftoken=([^;]+)/);
    return m ? m[1] : '';
}

function initModal() {
    if (modalHelper) return;
    modalHelper = createDynamicModal({
        modalId: 'contract-category-modal',
        onContentInjected: function (contentEl) {
            if (typeof window.initCustomSelects === 'function') {
                window.initCustomSelects();
            }
            if (typeof window.reinitFlowbiteWidgets === 'function') {
                var modalEl = document.getElementById('contract-category-modal');
                if (modalEl) window.reinitFlowbiteWidgets(modalEl);
            }
        },
    });
}

function getRoot() {
    return document.querySelector('[data-auto-init="contractCategorySettings"]');
}

function handleDelegatedClick(evt) {
    var t = evt.target;
    if (!t || !t.closest) return;

    var btn = t.closest('[data-contract-category-modal-open]');
    if (!btn) return;

    var root = getRoot();
    if (!root) return;
    if (!modalHelper) return;

    evt.preventDefault();

    var mode = btn.dataset.contractCategoryModalMode || 'create';
    var url;
    var title;

    if (mode === 'edit') {
        url = btn.dataset.contractCategoryEditUrl;
        title = root.dataset.contractCategoryModalEditTitle || 'Edit category';
    } else {
        url = root.dataset.contractCategoryCreateUrl;
        title = root.dataset.contractCategoryModalCreateTitle || 'Add category';
    }

    if (!url) return;
    modalHelper.open(url, title);
}

function postNewOrder(tbody) {
    var url = tbody.dataset.reorderUrl;
    if (!url) return;
    var rows = tbody.querySelectorAll('tr[data-cat-id]');
    var order = [];
    rows.forEach(function (tr) {
        var pk = parseInt(tr.dataset.catId, 10);
        if (!isNaN(pk)) order.push(pk);
    });
    if (order.length === 0) return;

    fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': getCsrfToken(),
            'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({ order: order }),
    }).then(function (resp) {
        if (!resp.ok) {
            console.warn('[contract-category-reorder] failed', resp.status);
        }
    }).catch(function (err) {
        console.warn('[contract-category-reorder] network error', err);
    });
}

function initSortableOn(tbody) {
    // Per-element guard — see rule 4 of hx-boost-init.mdc. Module-
    // level flag would survive every #list-results swap and silently
    // leave the freshly-swapped <tbody> without drag wiring.
    if (!tbody || tbody._ccSortableBound) return;
    tbody._ccSortableBound = true;
    Sortable.create(tbody, {
        handle: '.cc-drag-handle',
        // Archived rows have no .cc-drag-handle, so they can't initiate
        // a drag. ``filter`` adds belt-and-braces defence against an
        // operator picking up the row by its hover background.
        filter: '.cc-row-archived',
        animation: 150,
        ghostClass: 'bg-primary-50',
        chosenClass: 'bg-primary-100',
        onEnd: function () {
            postNewOrder(tbody);
        },
    });
}

function initAllSortables() {
    var tbodies = document.querySelectorAll('tbody[data-contract-category-reorder]');
    tbodies.forEach(initSortableOn);
}

function handleAfterSettle(evt) {
    var target = evt.detail && evt.detail.target;
    if (!target) return;

    // 1) Full-page boost swap → the modal shell DOM node was just
    //    swapped out; the cached helper is now pointing at an orphan.
    if (target.id === 'main-content') {
        modalHelper = null;
        initModal();
    }

    // 2) The HTMX toggle (archive / reactivate) swaps just the
    //    ``#list-results`` subtree, so a fresh ``<tbody>`` lands
    //    without going through the boosted nav. Re-init Sortable on
    //    any tbody we haven't bound yet (per-element flag, so the
    //    old swapped-out tbody's instance is just garbage-collected
    //    with its dead DOM).
    initAllSortables();
}

function bindGlobalDelegation() {
    // hx-boost-init.mdc rule 1 — module-level bindOnce flag, because
    // the listener target is ``document.body`` which survives every
    // ``#main-content`` swap. Without this guard, every boosted
    // navigation TO the settings page would stack a fresh click +
    // ``htmx:afterSettle`` listener on body.
    if (globalBound) return;
    globalBound = true;
    document.body.addEventListener('click', handleDelegatedClick);
    document.body.addEventListener('htmx:afterSettle', handleAfterSettle);
}

export function initContractCategorySettings() {
    modalHelper = null;
    bindGlobalDelegation();
    initModal();
    initAllSortables();
}

export function autoInitContractCategorySettings() {
    if (!getRoot()) return;
    initContractCategorySettings();
}
